import React, { useMemo, useState } from "react";
import Icon from "@imminently/decisively-ui/Icon";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Collapse,
} from "@mui/material";
import Button from "@imminently/decisively-ui/Button";
import moment from "moment";
import { iterateGraphNodes } from "@imminently/decisively-core";
import GraphProvider from "@imminently/decisively-ui/GraphProvider";
import { GraphNodeDebugDialog } from "@imminently/decisively-ui/GraphNodeDebugDialog";
import NodePath from "@imminently/decisively-ui/NodePath";
import { getAttributeDescription } from "@/common/AttributeDescription";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { useEditAttributeModal } from "@/pages/models/release/DataModel/route/Attributes/AttributeEditModal";
const ENABLE_DEBUG_DIALOG = false;


interface TriggerRowInfo {
  node: any;
  trigger: any;
  triggerIndex: number;
  wasExecuted: boolean;
  executionCount: number;
  executions: Array<{
    when: string;
    interaction: string;
  }>;
}

interface TriggersTabProps {
  session: any;
  release?: any;
}

const TriggersTab = ({ session, release: releaseProp }: TriggersTabProps) => {
  const fullRelease = useFullRelease();
  const release = releaseProp ?? fullRelease;
  const openEditModal = useEditAttributeModal();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedNode, setSelectedNode] = useState<any | null>(null);
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);

  const triggerRows = useMemo(() => {
    const rows: TriggerRowInfo[] = [];

    if (!session?.graph) {
      return rows;
    }

    iterateGraphNodes(session.graph, (node) => {
      if (node.hidden === true) {
        return;
      }

      if (node.triggers && Array.isArray(node.triggers) && node.triggers.length > 0) {
        for (let triggerIndex = 0; triggerIndex < node.triggers.length; triggerIndex++) {
          const trigger = node.triggers[triggerIndex];
          const triggered = node.triggered || [];

          // Filter executions to only this specific trigger index
          // Handle both new format (with explicit index) and legacy format (array position)
          const executions = triggered.filter((exec: any, arrayIdx: number) => {
            if (exec.index !== undefined) {
              return exec.index === triggerIndex;
            }
            // Legacy data: use array position
            return arrayIdx === triggerIndex;
          });

          const wasExecuted = executions.length > 0;

          rows.push({
            node,
            trigger,
            triggerIndex,
            wasExecuted,
            executionCount: executions.length,
            executions,
          });
        }
      }
    });

    // Sort by node description, then trigger index
    rows.sort((a, b) => {
      const aDesc = getAttributeDescription(a.node);
      const bDesc = getAttributeDescription(b.node);
      if (aDesc !== bDesc) {
        return aDesc.localeCompare(bDesc);
      }
      return a.triggerIndex - b.triggerIndex;
    });

    return rows;
  }, [session?.graph]);

  const toggleRowExpansion = (nodeKey: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeKey)) {
        newSet.delete(nodeKey);
      } else {
        newSet.add(nodeKey);
      }
      return newSet;
    });
  };

  const handleNodeClick = (node: any) => {
    if (ENABLE_DEBUG_DIALOG) {
      setSelectedNode(node);
      setDebugDialogOpen(true);
    }
  };

  const handleEditAttribute = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    openEditModal(nodeId);
  };

  const renderTable = () => {
    if (!triggerRows || triggerRows.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center" style={{ padding: "2rem" }}>
          No triggers found in this session.
        </Typography>
      );
    }

    return (
      <div className="flex flex-col h-full min-h-0">
        <TableContainer className="w-full flex-1 min-h-0 overflow-auto" style={{ maxHeight: "100%" }}>
          <Table stickyHeader={true}>
            <TableHead>
              <TableRow>
                <TableCell className="w-10" />
                <TableCell className="max-w-xs">Attribute</TableCell>
                <TableCell className="max-w-[120px]">Type</TableCell>
                <TableCell className="max-w-xs">Title</TableCell>
                <TableCell className="max-w-[120px]">Status</TableCell>
                <TableCell className="max-w-[120px]">Executions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {triggerRows.map((triggerRow, index) => {
                const { node, trigger, triggerIndex, wasExecuted, executionCount, executions } = triggerRow;
                const nodeDescription = getAttributeDescription(node);
                const nodeKey = `${node.path || node.id}-${triggerIndex}`;
                const isExpanded = expandedRows.has(nodeKey);

                return (
                  <React.Fragment key={nodeKey}>
                    <TableRow className={wasExecuted ? "bg-green-50" : ""}>
                      <TableCell>
                        {executionCount > 0 && (
                          <Button icon
                            size="sm"
                            label={isExpanded ? "Collapse row" : "Expand row"}
                            onClick={() => toggleRowExpansion(nodeKey)}
                          >
                            {isExpanded ? <Icon name="expand" /> : <Icon name="go-forward" />}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell
                        className={`max-w-xs ${ENABLE_DEBUG_DIALOG ? "cursor-pointer hover:bg-gray-50" : ""}`}
                        onClick={() => handleNodeClick(node)}
                      >
                        <div className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className={"block"}>
                              {nodeDescription}
                              <Button icon
                                size="sm"
                                label="Edit attribute"
                                onClick={(e) => handleEditAttribute(e, node.id)}
                                className="inline-block opacity-50 hover:opacity-100"
                              >
                                <Icon name="edit" />
                              </Button>
                            </div>

                            <div className="mt-1 overflow-x-auto">
                              <NodePath node={node} showDescription={false} />
                            </div>
                          </div>

                        </div>
                      </TableCell>
                      <TableCell className="max-w-[80px]">
                        <Chip
                          size="small"
                          label={trigger.type || "unknown"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {trigger.title || "—"}
                      </TableCell>

                      <TableCell className="max-w-[120px]">
                        <Chip
                          size="small"
                          label={wasExecuted ? "Executed" : "Not Executed"}
                          color={wasExecuted ? "primary" : "default"}
                          className={wasExecuted ? "!bg-green-500 !text-white" : "!bg-gray-500 !text-white"}
                        />
                      </TableCell>
                      <TableCell className="max-w-[120px]">
                        {executionCount > 0 ? `${executionCount} time${executionCount !== 1 ? "s" : ""}` : "—"}
                      </TableCell>
                    </TableRow>
                    {executionCount > 0 && (
                      <TableRow>
                        <TableCell className="!py-0" colSpan={6}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit={true}>
                            <div className="m-2">
                              <Typography variant="h6" gutterBottom={true} component="div">
                                Execution History
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>When</TableCell>
                                    <TableCell>Interaction ID</TableCell>
                                    <TableCell>Time Ago</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {executions.map((exec: any, execIdx: number) => {
                                    return (
                                      <TableRow key={execIdx}>
                                        <TableCell>{execIdx + 1}</TableCell>
                                        <TableCell>
                                          {exec.when ? moment(exec.when).format("YYYY-MM-DD HH:mm:ss") : "N/A"}
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption" className="font-mono">
                                            {exec.interaction || "N/A"}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          {exec.when ? moment(exec.when).fromNow() : "N/A"}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  return (
    <GraphProvider graph={session.graph}>
      {renderTable()}

      {ENABLE_DEBUG_DIALOG && selectedNode ? (
        <>
          {/* @ts-ignore release/graph come from ReleaseDependantProps/GraphDependantProps; types fail when package internals don't resolve */}
          <GraphNodeDebugDialog
            open={debugDialogOpen}
            onOpenChange={setDebugDialogOpen}
            defaultNode={selectedNode}
            release={release || undefined}
            graph={session.graph}
          />
        </>
      ) : null}
    </GraphProvider>
  );
};

export default TriggersTab;